import { assertSameRecordingIdentity } from './recordingIdentity.js';

export function firestoreRecordingValue(value) {
  if (value === null) return { nullValue: null };
  if (typeof value === 'boolean') return { booleanValue: value };
  if (typeof value === 'string') return { stringValue: value };
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Number.isSafeInteger(value) ? { integerValue: String(value) } : { doubleValue: value };
  }
  if (value instanceof Date || typeof value?.toDate === 'function') {
    return { timestampValue: (value instanceof Date ? value : value.toDate()).toISOString() };
  }
  if (Array.isArray(value)) return { arrayValue: { values: value.map(firestoreRecordingValue) } };
  if (value && typeof value === 'object' && (Object.getPrototypeOf(value) === Object.prototype
    || Object.getPrototypeOf(value) === null)) {
    return { mapValue: { fields: Object.fromEntries(Object.entries(value)
      .filter(([, item]) => item !== undefined).map(([key, item]) => [key, firestoreRecordingValue(item)])) } };
  }
  throw new Error('The recording contains an unsupported field value.');
}

/**
 * Firebase's create-only API needs no read of a nonexistent document, which
 * governed Domain Rules intentionally deny. A Firebase user token keeps the
 * request subject to the same Rules as SDK writes. No privileged token is used.
 */
export async function createFirestoreDocumentOnce({
  projectId, collectionPath, id, data, getToken,
  origin = 'https://firestore.googleapis.com', fetchImpl = globalThis.fetch,
}) {
  if (!projectId || !id || !collectionPath) throw new Error('The recording destination is missing.');
  const fields = firestoreRecordingValue(data).mapValue.fields;
  const token = await getToken();
  if (!token) throw new Error('Sign in before recording.');
  const path = collectionPath.split('/').map(encodeURIComponent).join('/');
  const url = `${origin}/v1/projects/${encodeURIComponent(projectId)}/databases/(default)/documents/${path}?documentId=${encodeURIComponent(id)}`;
  const response = await fetchImpl(url, {
    method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ fields }),
  });
  const body = await response.json();
  if (response.ok) return { id, wasCreated: true };
  if (response.status === 409 && body.error?.status === 'ALREADY_EXISTS') {
    return { id, wasCreated: false };
  }
  const error = new Error(body.error?.message || 'The recording could not be saved. Try again.');
  error.code = String(body.error?.status || 'unknown').toLowerCase().replaceAll('_', '-');
  throw error;
}

export async function createFirestoreRecordOnce(options) {
  const result = await createFirestoreDocumentOnce(options);
  if (result.wasCreated) return { ...options.data, ...result };
  const saved = await options.readExisting(result.id);
  if (!saved) throw new Error('The saved recording could not be read. Try again.');
  assertSameRecordingIdentity(saved, options.data);
  return { ...saved, ...result };
}
